import {
  Box,
  Flex,
  VStack,
  HStack,
  useBreakpointValue,
  useColorModeValue,
} from "@chakra-ui/react";

// Responsive Container
export const ResponsiveContainer = ({ children, maxW = "container.xl", ...props }) => {
  return (
    <Box
      maxW={maxW}
      mx="auto"
      px={{ base: 2, md: 4, lg: 6 }}
      {...props}
    >
      {children}
    </Box>
  );
};

// Responsive Grid
export const ResponsiveGrid = ({ 
  children, 
  columns = { base: 1, md: 2, lg: 3, xl: 4 },
  gap = 4,
  ...props 
}) => {
  return (
    <Box
      display="grid"
      gridTemplateColumns={{
        base: `repeat(${columns.base || 1}, 1fr)`,
        md: `repeat(${columns.md || 2}, 1fr)`,
        lg: `repeat(${columns.lg || 3}, 1fr)`,
        xl: `repeat(${columns.xl || 4}, 1fr)`,
      }}
      gap={gap}
      {...props}
    >
      {children}
    </Box>
  );
};

// Responsive Card
export const ResponsiveCard = ({ 
  children, 
  p = { base: 3, md: 4, lg: 6 },
  ...props 
}) => {
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  return (
    <Box
      bg={bgColor}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="md"
      p={p}
      shadow="sm"
      _hover={{ shadow: "md" }}
      transition="all 0.2s"
      {...props}
    >
      {children}
    </Box>
  );
};

// Responsive Header
export const ResponsiveHeader = ({ 
  title, 
  subtitle, 
  actions,
  direction = { base: "column", md: "row" },
  ...props 
}) => {
  return (
    <Flex
      justify="space-between"
      align="center"
      direction={direction}
      gap={4}
      mb={6}
      {...props}
    >
      <Box>
        <Box
          fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
          fontWeight="bold"
          letterSpacing="-0.5px"
        >
          {title}
        </Box>
        {subtitle && (
          <Box
            fontSize={{ base: "xs", md: "sm" }}
            color="gray.500"
            mt={1}
          >
            {subtitle}
          </Box>
        )}
      </Box>
      {actions && (
        <Flex
          direction={{ base: "column", sm: "row" }}
          gap={2}
          align={{ base: "stretch", sm: "center" }}
        >
          {actions}
        </Flex>
      )}
    </Flex>
  );
};

// Responsive Table Wrapper
export const ResponsiveTableWrapper = ({ children, ...props }) => {
  const bgColor = useColorModeValue("white", "gray.800");
  
  return (
    <Box
      bg={bgColor}
      borderRadius="md"
      overflowX="auto"
      shadow="sm"
      border="1px solid"
      borderColor={useColorModeValue("gray.200", "gray.700")}
      {...props}
    >
      {children}
    </Box>
  );
};

// Responsive Button Group
export const ResponsiveButtonGroup = ({ children, ...props }) => {
  return (
    <HStack
      spacing={{ base: 2, md: 3 }}
      direction={{ base: "column", sm: "row" }}
      align={{ base: "stretch", sm: "center" }}
      {...props}
    >
      {children}
    </HStack>
  );
};

// Responsive Form Layout
export const ResponsiveFormLayout = ({ children, ...props }) => {
  return (
    <VStack
      spacing={6}
      align="stretch"
      maxW={{ base: "100%", md: "600px", lg: "800px" }}
      mx="auto"
      {...props}
    >
      {children}
    </VStack>
  );
};

// Responsive Sidebar Layout
export const ResponsiveSidebarLayout = ({ 
  main, 
  sidebar, 
  sidebarWidth = "300px",
  ...props 
}) => {
  const sidebarDisplay = useBreakpointValue({ base: "none", lg: "block" });
  const mainMargin = useBreakpointValue({ base: 0, lg: sidebarWidth });

  return (
    <Flex gap={6} {...props}>
      <Box flex="1" ml={mainMargin}>
        {main}
      </Box>
      <Box
        display={sidebarDisplay}
        width={sidebarWidth}
        position="fixed"
        right={0}
        top="4rem"
        height="calc(100vh - 4rem)"
        overflowY="auto"
        p={4}
      >
        {sidebar}
      </Box>
    </Flex>
  );
};

// Responsive Stats Grid
export const ResponsiveStatsGrid = ({ stats, ...props }) => {
  return (
    <ResponsiveGrid
      columns={{ base: 1, sm: 2, lg: 4 }}
      gap={4}
      mb={6}
      {...props}
    >
      {stats.map((stat, index) => (
        <ResponsiveCard key={index} p={4}>
          <VStack align="stretch" spacing={2}>
            <Box fontSize="sm" color="gray.500">
              {stat.label}
            </Box>
            <Box fontSize="2xl" fontWeight="bold">
              {stat.value}
            </Box>
            {stat.change && (
              <Box fontSize="xs" color={stat.change > 0 ? "green.500" : "red.500"}>
                {stat.change > 0 ? "+" : ""}{stat.change}%
              </Box>
            )}
          </VStack>
        </ResponsiveCard>
      ))}
    </ResponsiveGrid>
  );
};

// Responsive Navigation
export const ResponsiveNavigation = ({ items, activeItem, onItemClick, ...props }) => {
  return (
    <Flex
      direction={{ base: "column", md: "row" }}
      gap={2}
      {...props}
    >
      {items.map((item) => (
        <Box
          key={item.id}
          px={4}
          py={2}
          borderRadius="md"
          cursor="pointer"
          bg={activeItem === item.id ? "blue.500" : "transparent"}
          color={activeItem === item.id ? "white" : "inherit"}
          _hover={{
            bg: activeItem === item.id ? "blue.600" : "gray.100",
          }}
          onClick={() => onItemClick(item.id)}
        >
          {item.label}
        </Box>
      ))}
    </Flex>
  );
};

const ResponsiveWrapper = {
  ResponsiveContainer,
  ResponsiveGrid,
  ResponsiveCard,
  ResponsiveHeader,
  ResponsiveTableWrapper,
  ResponsiveButtonGroup,
  ResponsiveFormLayout,
  ResponsiveSidebarLayout,
  ResponsiveStatsGrid,
  ResponsiveNavigation,
};

export default ResponsiveWrapper; 